import type { TFunction } from 'i18next';
import { when } from 'mobx';

import type { CustomFields, IPredicate, TComparison, TPredicateKind } from '@feathr/blackbox';
import { FieldCollection, FieldDataType, Segment } from '@feathr/blackbox';
import type { ISelectOption } from '@feathr/components';
import { moment } from '@feathr/hooks';
import type { Model } from '@feathr/rachis';

import defaultBreadcrumbFields from '@feathr/blackbox/fixtures/defaultBreadcrumbFields.json';
import defaultPersonFields from '@feathr/blackbox/fixtures/defaultPersonFields.json';

const { bool, date, float, int, list, str } = FieldDataType;

const UNSUBSCRIBE_MESSAGE_VALUE = '"global": true';

export type TOptionsGroup =
  | 'campaign'
  | 'flight'
  | 'segment'
  | 'form'
  | 'campaignGoal'
  | 'flightGoal'
  | 'facebookCampaign'
  | 'pinpoint';

interface IGetActivitiesArgs {
  CustomFields: CustomFields;
  model: Model;
  isAnyFeathrPlatform: boolean;
  optionsGroup: TOptionsGroup;
  t: TFunction;
}

export interface IAttrOption extends ISelectOption {
  helpText: string;
  kind: FieldCollection.Breadcrumb | FieldCollection.Person;
  type: FieldDataType;
}

export interface IComparisonOption {
  id: TComparison;
  name: string;
}

export function getComparisonOptions(
  { id, type }: Pick<IAttrOption, 'id' | 'type'>,
  t: TFunction,
): IComparisonOption[] {
  const {
    array,
    equality,
    existence,
    numerical,
    string,
    temporal,
  }: Record<string, IComparisonOption[]> = {
    equality: [
      { id: 'eq', name: t('is') },
      { id: 'ne', name: t('is not') },
    ],
    existence: [
      { id: 'exists', name: t('exists') },
      { id: 'nexists', name: t('does not exist') },
    ],
    array: [
      { id: 'in', name: t('contains') },
      { id: 'nin', name: t('does not contain') },
    ],
    numerical: [
      { id: 'eq', name: t('equals') },
      { id: 'ne', name: t('does not equal') },
      { id: 'gt', name: t('is greater than') },
      { id: 'lt', name: t('is less than') },
    ],
    string: [
      { id: 'starts_with', name: t('starts with') },
      { id: 'wildcard', name: t('matches pattern') },
      { id: 'regexp', name: t('matches the expression') },
      { id: 'contains_substring', name: t('contains') },
    ],
    temporal: [
      { id: 'gt', name: t('is after') },
      { id: 'lt', name: t('is before') },
      { id: 'gte', name: t('is after or on') },
      { id: 'lte', name: t('is before or on') },
    ],
  };

  switch (type) {
    case str: {
      const baseOptions = [...existence, ...equality];
      // Don't allow string comparisons for ObjectId fields.
      if (id.includes('_id')) {
        return [...baseOptions, ...array];
      }
      return [...baseOptions, ...string];
    }

    case int:

    case float:
      return [...existence, ...numerical];

    case bool:
      return equality;

    case date:
      return [...existence, ...equality, ...temporal];

    case list:
      return [...existence, ...array];

    default:
      return existence;
  }
}

export async function getAttributes(
  t: TFunction,
  CustomFields: CustomFields,
): Promise<IAttrOption[]> {
  const helpText = t('This is a custom field that has been added to your Feathr database.');

  const customAttributes = await CustomFields.list({
    filters: {
      collection: FieldCollection.Person,
    },
    pagination: {
      page: 0,
      page_size: 1000,
    },
  });

  await when(() => !customAttributes.isPending);
  const options = customAttributes.models.map((field) => ({
    helpText: field.get('description', helpText),
    id: `custom.${field.get('f_key')}`,
    kind: field.get('collection'),
    name: field.get('u_key'),
    type: field.get('data_type'),
  }));

  return [...defaultPersonFields, ...options];
}

/*
 * Check if any entry in model.predicates[0].group has the required conditions.
 * Attribute being 'msg' and value being '"global": true' means that the user has unsubscribed from all messages from a campaign.
 */
export function hasGlobalMsgCondition(model: Model): boolean {
  if (!(model instanceof Segment)) {
    return false;
  }
  const predicates = model.get('predicates');
  let firstPredicate: IPredicate | undefined;

  if (predicates && predicates.length > 0) {
    firstPredicate = predicates[0];
  } else {
    firstPredicate = undefined;
  }

  if (firstPredicate && firstPredicate.group) {
    return firstPredicate.group.some(
      (entry: IPredicate) =>
        entry.value === UNSUBSCRIBE_MESSAGE_VALUE && entry.attr_against === 'msg',
    );
  }

  return false;
}

export async function getActivities({
  CustomFields,
  model,
  isAnyFeathrPlatform,
  optionsGroup,
  t,
}: IGetActivitiesArgs): Promise<IAttrOption[]> {
  const helpText = t('This is a custom field that has been added to your Feathr database.');

  const customActivities = CustomFields.list({
    filters: {
      collection: FieldCollection.Breadcrumb,
    },
    pagination: {
      page: 0,
      page_size: 1000,
    },
  });

  await when(() => !customActivities.isPending);
  const options = customActivities.models.map((field) => ({
    helpText: field.get('description', helpText),
    id: `cust_params.${field.get('f_key')}`,
    kind: field.get('collection'),
    name: field.get('u_key'),
    type: field.get('data_type'),
  }));

  function shouldRender(field: ISelectOption): boolean {
    const render: boolean = model instanceof Segment ? model.get('read_only') : false;
    const globalMsgCondition: boolean = hasGlobalMsgCondition(model);
    const gated = [
      {
        id: 'p_id',
        render: isAnyFeathrPlatform,
      },
      {
        id: 'q_id',
        render,
      },
      {
        id: 'msg',
        render: render || globalMsgCondition,
      },
    ];

    const gatedAttribute = gated.find(({ id }) => id === field.id);

    // If it's a gated attribute, we want to check its render conditions.
    if (gatedAttribute) {
      return gatedAttribute.render;
    } else if (optionsGroup === 'pinpoint') {
      /**
       * There are certain activity predicates we don't want used
       * in email campaign groups.
       */
      return !['brow', 'pform', 'd_c', 'isp'].includes(field.id);
    } else {
      return true;
    }
  }

  return [...defaultBreadcrumbFields.filter(shouldRender), ...options];
}

export function toPredicateAttrType(
  type: FieldDataType,
  comparison?: TComparison,
): 'string' | 'boolean' | 'list' | 'date' | 'integer' | 'float' {
  // Override attribute type for array comparisons.
  if (comparison && ['in', 'nin'].includes(comparison)) {
    return 'list';
  }

  switch (type) {
    case str:
      return 'string';

    case int:
      return 'integer';

    case bool:
      return 'boolean';

    default:
      return type;
  }
}

export function shouldShowValueInput({ comparison }: IPredicate): boolean {
  // Don't show value input for existence comparisons.
  if (!comparison || ['exists', 'nexists'].includes(comparison)) {
    return false;
  }
  return true;
}

export function fieldToKind(
  fieldCollection: FieldCollection.Breadcrumb | FieldCollection.Person | 'Pinpoint',
): TPredicateKind {
  const { Breadcrumb, Person } = FieldCollection;
  const map = {
    [Breadcrumb]: 'activity',
    [Person]: 'attribute',
    Pinpoint: 'update',
  };

  return map[fieldCollection] as TPredicateKind;
}

export interface IRelativeDateOptions {
  daysAgo: ISelectOption;
  daysFromNow: ISelectOption;
}

export function getRelativeDateOptionsMap(
  t: TFunction,
  value: string | number,
): IRelativeDateOptions {
  // Only map the options if the count is a number (predicate value can be a string).
  const count = typeof value === 'number' ? value : 1;

  const relativeDateOptions = [
    { id: '-', name: t('day ago', { count }) },
    { id: '+', name: t('day from now', { count }) },
  ];

  return {
    daysAgo: relativeDateOptions[0],
    daysFromNow: relativeDateOptions[1],
  };
}

export interface IDateComparisonOptions {
  today: ISelectOption;
  exactDate: ISelectOption;
  exactDateAndTime: ISelectOption;
  relativeDate: ISelectOption;
}

export function getDateComparisonOptionsMap(t: TFunction): IDateComparisonOptions {
  const dateComparisonOptions = [
    {
      name: t('today'),
      /*
       * Maps to a `_date_r` comparison with a 0 value and does not
       * reference its own unique comparison operator.
       */
      id: 'today',
    },
    {
      name: t('exact date'),
      // Maps to new comparison operators: eq_date, ne_date, gt_date and lt_date.
      id: '_date',
    },
    {
      name: t('exact date and time'),
      // Maps to the existing comparison operators, eq, ne, gt and lt.
      id: 'date_time',
    },
    {
      name: t('relative date'),
      // Maps to new comparison operators: eq_date_r, ne_date_r, gt_date_r and lt_date_r.
      id: '_date_r',
    },
  ];

  return {
    today: dateComparisonOptions[0],
    exactDate: dateComparisonOptions[1],
    exactDateAndTime: dateComparisonOptions[2],
    relativeDate: dateComparisonOptions[3],
  };
}

export function getInitialValueForDate(
  comparison: string,
  currentValue?: string | number | boolean | string[],
): string | number {
  if (['exists', 'nexists'].includes(comparison)) {
    return '';
  }

  // Filter out incompatible types for date handling
  const validCurrentValue =
    typeof currentValue === 'string' || typeof currentValue === 'number' ? currentValue : undefined;

  const now = moment.utc();
  if (comparison.endsWith('_date')) {
    // Returns ISO format with current date only
    return now.startOf('day').format();
  }

  if (!comparison.includes('_')) {
    // Returns ISO format with current time minus seconds and milliseconds
    return now.seconds(0).milliseconds(0).format();
  }

  // Handle "today" case - if comparison ends with _date_r and current value is 0, preserve it
  if (comparison.endsWith('_date_r') && validCurrentValue === 0) {
    return 0;
  }

  return 1;
}
